import { useCallback, useEffect, useMemo, useState } from "react";
import { useActions } from "app/utils/hooks";
import Fuse from "../../../../vendor/fuse";
import SpaceModel from "app/models/SpaceModel";
import { SpaceSelectProps } from "./index.types";
import { useDispatch, useSelector } from "react-redux";
import {
  locations,
  spaceObj,
  applyTemplateToSpace,
} from "app/actions/applyTemplate";
import { RootState } from "typedefs";

const initSpaces: SpaceModel[] = [];

const useSpaceSelectModal = ({ onClose, templateId }: SpaceSelectProps) => {
  const dispatch = useDispatch();

  const space = useSelector((state: RootState) => state.space.data);

  const spaceIds = useSelector(
    (state: RootState) => state.applyTemplate.locations
  );
  const spacesArr = useSelector(
    (state: RootState) => state.applyTemplate.spaceObj
  );

  const applyTemplateActions = useActions({
    applyTemplateToSpace,
  });
  const [searchTerm, setSearch] = useState<string>("");
  const [spaces, setSpaces] = useState(initSpaces);
  const [selectedSpacesId, setSelectedSpacesId] = useState<string[]>(spaceIds);
  const [selectedSpace, setSelectedSpace] = useState<SpaceModel[]>(spacesArr);

  const fuseOptions = {
    keys: ["athleteName", "name"],
    includeScore: true,
    shouldSort: true,
    threshold: 0.5,
  };

  useEffect(() => {
    if (space != null) {
      setSpaces(space || []);
    }
  }, [space]);

  const fuse = useMemo(() => {
    const items = spaces.map((space) => space);
    return new (Fuse as any)(items, fuseOptions);
  }, [spaces, fuseOptions]);

  const results = useMemo(() => {
    if (searchTerm !== "") {
      return fuse.search(searchTerm).map((result: any) => result.item);
    } else {
      return spaces;
    }
  }, [searchTerm, spaces, fuse]);

  const onSearchSpace = useCallback(
    (value: string) => {
      if (value != null && value.length > 0) {
        setSearch(value);
      } else {
        setSearch("");
      }
    },
    [searchTerm]
  );

  const spaceSelectHandler = useCallback(
    (value: string, spaceObj: SpaceModel) => {
      setSelectedSpacesId((prevSelectedSpacesId) => {
        if (prevSelectedSpacesId.includes(value)) {
          return prevSelectedSpacesId.filter((val) => val !== value);
        } else {
          return [...prevSelectedSpacesId, value];
        }
      });
      setSelectedSpace((prevSelectedSpace) => {
        if (prevSelectedSpace.includes(spaceObj)) {
          return prevSelectedSpace.filter((val) => val !== spaceObj);
        } else {
          return [...prevSelectedSpace, spaceObj];
        }
      });
    },
    [selectedSpacesId, selectedSpace]
  );

  useEffect(() => {
    dispatch(locations(selectedSpacesId));
    dispatch(spaceObj(selectedSpace));
  }, [selectedSpacesId, selectedSpace]);

  const cancelHandler = () => {
    setSelectedSpacesId([]);
    setSelectedSpace([]);
    setSearch("");
    onClose();
  };

  const applyHandler = () => {
    applyTemplateActions.applyTemplateToSpace(selectedSpacesId, templateId);
    setSearch("");
    onClose();
  };

  return {
    spaceSelectHandler,
    cancelHandler,
    applyHandler,
    onSearchSpace,
    space,
    searchTerm,
    results,
    selectedSpacesId,
  };
};

export default useSpaceSelectModal;
